import { forwardRef, FunctionComponent, ReactNode, Ref, PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import { ForwardProps } from '../../types';

interface TabPanelProps {
  /** The id of the Tab that the tab panel is related to. */
  tabId: string;
  /**
   * The current selected value of the tabs. Controls the visibility of the tab panel.
   * The tabsValue must match the tabId in order for the TabPanel to show itself.
   * */
  currentTabId?: string;
  /** Content of the tab panel. */
  children: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const defaultProps: Partial<TabPanelProps> = {
  tabId: ''
};

export const StyledTabPanel = styled.div<{ current: boolean }>(({ current }) => {
  return css`
    min-width: 0;
    max-width: 100%;

    :focus {
      outline: none;
    }

    ${!current &&
    css`
      display: none;
    `}
  `;
});

const TabPanel: FunctionComponent<TabPanelProps & ForwardProps> = forwardRef(
  (
    { tabId, currentTabId, children, ...restProps }: PropsWithoutRef<TabPanelProps>,
    ref: TabPanelProps['ref']
  ) => {
    const getTabIndex = () => {
      if (currentTabId && tabId) {
        return currentTabId === tabId ? 0 : undefined;
      }
      return 0;
    };
    return (
      <StyledTabPanel
        {...restProps}
        current={currentTabId && tabId ? currentTabId === tabId : true}
        role='tabpanel'
        tabIndex={getTabIndex()}
        aria-labelledby={`${tabId}`}
        id={`${tabId}-panel`}
        ref={ref}
      >
        {children}
      </StyledTabPanel>
    );
  }
);

TabPanel.defaultProps = defaultProps;

export default TabPanel;
